import { getChartSourceFromWidgetType, WidgetType } from "components/dashboard/utils/widgetConfig";
import { APP_CONFIG } from "config/appConfig";
import {
    CHART_ERROR_MESSAGE,
    CHART_NAME_PATTERN,
    CreateChartState,
} from "..";
import { getMQTTQueryByDevice } from "../../utils/widgetUtil";
import { LDSUDevice } from "./LDSUWidgetSensorList";
import { CHART_DUPLICATE_COLOR_MESSAGE } from "constant/messages";
import { GATEWAY_LABEL, LDSU_UUID_LABEL } from "constant/displayLabels";

const ldsuDisplayName = APP_CONFIG.APP_IS_ONPREM
    ? "LDSU/Auxiliary Hub"
    : "LDSU";

export type DeviceAttribute = {
    gateway_id: string;
    device_id: string;
    said: number;
    time_series: string;
    name: string;
    color: string;
    unit: string;
};

// Build attributes from sensors and actuators
const buildAttributes = (
    sensors: LDSUDevice[],
    actuators: LDSUDevice[]
): DeviceAttribute[] => {
    const sensorAttrs: DeviceAttribute[] = sensors.map((sensor) => ({
        gateway_id: sensor.gatewayId,
        device_id: sensor.ldsu_uuid,
        said: sensor.SAID,
        time_series: getMQTTQueryByDevice(sensor),
        name: sensor.sensor_type,
        color: sensor.color,
        unit: sensor.unit || "",
    }));

    const actuatorAttrs: DeviceAttribute[] = actuators.map((actuator) => ({
        gateway_id: actuator.gatewayId,
        device_id: actuator.ldsu_uuid,
        said: actuator.SAID,
        time_series: getMQTTQueryByDevice(actuator),
        name: actuator.sensor_type,
        color: actuator.color,
        unit: actuator.unit || "",
    }));

    return [...sensorAttrs, ...actuatorAttrs];
};

export const LDSUChartResultTemplate: CreateChartState = {
    title: "Creating New Widget(s)",
    data: [],
    columns: [
        {
            key: "chart_name",
            name: "Widget Name",
        },
        {
            key: "ldsu_name",
            name: ldsuDisplayName,
        },
        {
            key: "gateway_name",
            name: GATEWAY_LABEL,
        },
        {
            key: "ldsu_uuid",
            name: LDSU_UUID_LABEL,
        },
    ],
    transform: (data: any, widgetType: WidgetType) => {
        // Build attributes from sensors and actuators
        const attributes = buildAttributes(
            data.sensors || [],
            data.actuators || []
        );
        const { source, chart } = getChartSourceFromWidgetType(widgetType);

        return {
            attributes,
            source: source,
            name: data.chart_name,
            chart: chart,
        };
    },
    validate() {
        const validates = this.data.map((item) => ({
            isValid: CHART_NAME_PATTERN.test((item.chart_name ?? "").trim()),
            message: CHART_ERROR_MESSAGE,
        }));

        const validateColor = () => {
            const item = this.data[0];
            const attributes = buildAttributes(
                item.sensors || [],
                item.actuators || []
            );
            const uniqueColors = Array.from(
                new Set(
                    attributes.map((attr: DeviceAttribute) =>
                        attr.color.toLowerCase()
                    )
                )
            );
            return {
                isValid: uniqueColors.length === attributes.length,
                message: CHART_DUPLICATE_COLOR_MESSAGE,
            };
        };

        return [...validates, validateColor()];
    },
};

export const defaultLDSUWidgetConfig = {
    isCombined: false,
    defaultWidgetName: `${ldsuDisplayName} Overview`,
    maxItems: undefined,
};
